import { useTranslation } from 'react-i18next';
import { CellEditPopover } from '~/components/ui/table/CellEditPopover';
import { EditCellForm } from '~/components/ui/table';
import { FieldError } from '~/components/ui/inputs/FieldError';
import { bundleSizeMessageParams, parseBundleSize } from '~/utils/bundleBounds';
import { useInlineEdit } from '~/pages/admin-inventory-classification/useInlineEdit';

type ProductPiecesPerBundleEditorProps = Readonly<{
  productId: string;
  piecesPerBundle: number | null;
  onSave: SaveBundleSize | null;
}>;

type SaveBundleSize = (piecesPerBundle: number) => Promise<boolean>;

type BundleSizeEditProps = Readonly<{
  productId: string;
  shownValue: number | null;
  onSave: SaveBundleSize;
}>;

function BundleSizeEdit({
  productId,
  shownValue,
  onSave,
}: BundleSizeEditProps) {
  const { t } = useTranslation('inventory-classification');
  const edit = useInlineEdit(parseBundleSize, onSave);

  return (
    <>
      <CellEditPopover
        isOpen={edit.isOpen}
        onOpen={edit.open}
        onDone={edit.close}
        isBusy={edit.isSaving}
        columnLabel={t('bundle.edit_label')}
        testId={`product-pieces-per-bundle-edit-${productId}`}
        renderEditor={() => (
          <EditCellForm
            defaultValue={shownValue ?? ''}
            editableType="number"
            isSubmitting={edit.isSaving}
            onSubmit={edit.submit}
            onCancel={edit.close}
            testIds={{
              input: `product-pieces-per-bundle-input-${productId}`,
              submit: `product-pieces-per-bundle-submit-${productId}`,
            }}
          />
        )}
      />
      {edit.hasError ? (
        <FieldError testId={`product-pieces-per-bundle-error-${productId}`}>
          {t('bundle.error', bundleSizeMessageParams)}
        </FieldError>
      ) : null}
    </>
  );
}

export function ProductPiecesPerBundleEditor({
  productId,
  piecesPerBundle,
  onSave,
}: ProductPiecesPerBundleEditorProps) {
  const { t } = useTranslation('inventory-classification');
  const shownPiecesPerBundle =
    piecesPerBundle !== null && piecesPerBundle > 0 ? piecesPerBundle : null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-neut-prim">
      <span>{t('bundle.product_label')}</span>
      <span
        className="font-medium"
        data-testid={`product-pieces-per-bundle-value-${productId}`}
      >
        {shownPiecesPerBundle ?? t('bundle.not_set')}
      </span>
      {onSave ? (
        <BundleSizeEdit
          productId={productId}
          shownValue={shownPiecesPerBundle}
          onSave={onSave}
        />
      ) : null}
    </div>
  );
}
